import { z } from 'zod'
import { cn } from '@/utils/style.ts'
import { Button } from '@/common/components/ui/button.tsx'
import { FormWrapper } from '@/common/components/Form/FormWrapper.tsx'
import React, { useEffect, useState } from 'react'
import { useForm, useFormContext } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { FormSection } from '@/common/components/Form/FormSection.tsx'
import { useCreateTermSheetMutation } from '@/features/document-autofill/document-autofill.api.ts'
import { FormMoneyInput } from '@/common/components/Form/FormMoneyInput.tsx'
import { FormNumberInput2 } from '@/common/components/Form/FormNumberInput2.tsx'
import { FormInput } from '@/common/components/Form/FormInput.tsx'
import { FormDatePicker2 } from '@/common/components/Form/FormDatePicker2.tsx'
import { downloadJsonFile, termSheetFileName } from '@/utils/misc.ts'
import { PageLayout } from '@/common/layouts/PageLayout.tsx'
import {
  buildRiskParametersSchema,
  RiskParametersBuildArgs,
  RiskParametersSection,
} from '@/pages/document-autofill/contract-sections/risk-parameters.tsx'
import {
  buildPersonalInfoSchema,
  PersonalInfoSchemaBuildArgs,
  PersonalInfoSection,
} from '@/pages/document-autofill/contract-sections/personal-info.tsx'
import { FormSwitch } from '@/common/components/Form/FormSwitch.tsx'
import { isNil } from 'lodash-es'
import { DateString, Money, Percent } from '@/common/types/misc.def.ts'
import { FormSelectAntd } from '@/common/components/Form/FormSelectAntd.tsx'
import { Typography } from 'antd'
import { captureScreenshot } from '@/features/document-autofill/contracts/screenshot.tsx'
import { FormMultiAdditionalBonus } from '@/pages/document-autofill/contract-sections/multi-additional-bonus.tsx'
import { TermSheetImport } from '@/pages/document-autofill/components/term-sheet-import.tsx'
import { useTermSheetConfig } from '@/pages/document-autofill/hooks/useTermSheetConfig.tsx'
import { Country } from '@/common/types/work-info.def.ts'

const riskParametersArgs: RiskParametersBuildArgs = {
  needTermSheetExtra: true,
  needHasGmv: true,
  needHasIntellectualProperty: true,
  needBuyingPowerRequireApproval: true,
}

const personalInfoArgs: PersonalInfoSchemaBuildArgs = {
  needPreferredName: false,
}

export const pmTermSheetSchema = z.object({
  meta: z.object({
    type: z.string().optional(),
    region: z.string().optional(),
    candidateType: z.string().optional(),
    isHiringManagerPrincipal: z.boolean().optional(),
  }).optional(),
  personalInfo: buildPersonalInfoSchema(personalInfoArgs),
  employmentDetails: z.object({
    startDate: DateString,
    position: z.string().min(1),
    location: z.string().min(1),
  }),
  financialTerms: z.object({
    salary: Money,
    centerBookPayoutRate: Percent,
    relocation: Money,
    signingBonus: Money.extend({
      subjectToPrevIncomeProof: z.boolean().optional(),
      payableOn: z.string(),
    }).nullable().optional(),
    additionalBonus: z.object({
      subjectToClawback: z.boolean(),
      subjectToPrevIncomeProof: z.boolean(),
      paymentYears: z.array(z.object({
        currency: z.string(),
        year: z.coerce.number().nonnegative().int(),
        yearPayoutAmount: z.coerce.number().nonnegative().int(),
        yearPayableOn: z.string(),
        yearConditions: z.string(),
        cnYearConditions: z.string().optional(),
      })).nullable().optional(),
    }).nullable().optional(),

    performanceBonus: z.object({
      payoutRate: Percent.optional(),
      revisedPayoutRate1: z.object({
        enabled: z.boolean().optional(),
        rate: Percent.optional(),
        bpThresholdMm: z.coerce.number().nonnegative().optional(),
      }).optional(),
      revisedPayoutRate2: z.object({
        enabled: z.boolean().optional(),
        rate: Percent.optional(),
        bpThresholdMm: z.coerce.number().nonnegative().optional(),
      }).nullable().optional(),
    }).nullable().optional().superRefine((data, ctx) => {
      if (isNil(data?.payoutRate)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Required', path: ['payoutRate'] })
      }
    }),
  }),
  deferredProgram: z.object({
    deferredRate: Percent,
    compensationThreshold: Money,
    vestedYears: z.coerce.number().min(1).int(),
    fundName: z.string().min(1),
  }),
  teamHiring: z.object({
    usCustomFreeText: z.string().optional(),
    expenseCap: Money,
    initialHeadcount: z.coerce.number().optional(),
    hiringLimitDate: DateString.optional(),
    hiringLimitBpThresholdMm: z.coerce.number().optional(),
  }),
  riskParameters: buildRiskParametersSchema(riskParametersArgs),
}).superRefine((data, ctx) => {
  if (data.teamHiring.expenseCap) {
    if (isNil(data.teamHiring.initialHeadcount)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Required', path: ['teamHiring.initialHeadcount'] })
    }
    if (isNil(data.teamHiring.hiringLimitBpThresholdMm)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Required', path: ['teamHiring.hiringLimitBpThresholdMm'] })
    }
  }
})

export type PmTermSheetParam = z.infer<typeof pmTermSheetSchema>

const PmTermSheetFinancialTerms = () => {
  const config = useTermSheetConfig()
  const { watch, setValue } = useFormContext()

  const watchPayoutRate = parseFloat(watch('financialTerms.performanceBonus.payoutRate'))
  const payoutRate1Disabled = watch('financialTerms.performanceBonus.revisedPayoutRate1.enabled') !== true
  const payoutRate2Disabled = watch('financialTerms.performanceBonus.revisedPayoutRate2.enabled') !== true
  const signingBonusDisabled = watch('financialTerms.signingBonus') === null

  useEffect(() => {
    if (payoutRate1Disabled) {
      setValue('financialTerms.performanceBonus.revisedPayoutRate1.rate', undefined)
      setValue('financialTerms.performanceBonus.revisedPayoutRate1.bpThresholdMm', undefined)
      setValue('financialTerms.performanceBonus.revisedPayoutRate2.enabled', false)
    }
  }, [payoutRate1Disabled, setValue])

  useEffect(() => {
    if (payoutRate2Disabled) {
      setValue('financialTerms.performanceBonus.revisedPayoutRate2.rate', undefined)
      setValue('financialTerms.performanceBonus.revisedPayoutRate2.bpThresholdMm', undefined)
    }
  }, [payoutRate2Disabled, setValue])

  return (
    <>
      <FormSection name="financialTerms">
        <FormMoneyInput name="salary" label="Annual Salary" currencyOptions={config.baseSalaryCcyOptions} />
        <FormNumberInput2
          label="Alpha Capture Program"
          name="centerBookPayoutRate"
          variant="percent"
          description="Put zero if not applicable"
        />
        <FormMoneyInput
          name="relocation"
          currencyOptions={config.baseSalaryCcyOptions}
          description="Put zero if not applicable"
        />
      </FormSection>
      <FormSection name="financialTerms.signingBonus" optional>
        <FormSwitch name="subjectToPrevIncomeProof" disabled={signingBonusDisabled} />
        <FormSelectAntd
          name="currency"
          options={config.bonusCcyOptions.map(x => ({ value: x, label: x }))}
          disabled={signingBonusDisabled}
        />
        <FormNumberInput2 name="amount" disabled={signingBonusDisabled} />
        <FormInput name="payableOn" disabled={signingBonusDisabled} description="E.g. First payroll run" />
      </FormSection>
      <FormMultiAdditionalBonus name={'financialTerms.additionalBonus'} currencyOptions={config.bonusCcyOptions} />
      <FormSection name="financialTerms.performanceBonus">
        <FormNumberInput2 variant="percent" name="payoutRate" description="Put zero if not applicable" />
        <FormSwitch
          name="revisedPayoutRate1.enabled"
          label="Revised PayoutRate 1 Enabled"
          disabled={!watchPayoutRate}
        />
        <FormNumberInput2
          name="revisedPayoutRate1.rate"
          label="Revised PayoutRate 1 Rate"
          variant="percent"
          disabled={payoutRate1Disabled}
        />
        <FormNumberInput2
          name="revisedPayoutRate1.bpThresholdMm"
          label="Revised PayoutRate 1 BP Threshold ($MM)"
          disabled={payoutRate1Disabled}
          description="In USD million"
        />
        <FormSwitch
          name="revisedPayoutRate2.enabled"
          label="Revised PayoutRate 2 Enabled"
          disabled={payoutRate1Disabled}
        />
        <FormNumberInput2
          name="revisedPayoutRate2.rate"
          label="Revised PayoutRate 2 Rate"
          variant="percent"
          disabled={payoutRate2Disabled}
        />
        <FormNumberInput2
          name="revisedPayoutRate2.bpThresholdMm"
          label="Revised PayoutRate 2 BP Threshold ($MM)"
          disabled={payoutRate2Disabled}
          description="In USD million"
        />
      </FormSection>
    </>
  )
}
export const PmTermSheet: React.FC = () => {
  const config = useTermSheetConfig()
  const [createTermSheet, { isLoading }] = useCreateTermSheetMutation()
  const [showDefaultTeamHiring, setShowDefaultTeamHiring] = useState<boolean>(true)
  const form = useForm<z.infer<typeof pmTermSheetSchema>>({
    resolver: zodResolver(pmTermSheetSchema),
    defaultValues: {
      deferredProgram: {
        deferredRate: 0.25,
        compensationThreshold: {
          amount: 500000,
          currency: 'USD',
        },
        vestedYears: 3,
      },
      riskParameters: {
        specialNotes: 'Assuming minimum 50% of BP utilization. Otherwise draw down limit will be calculated based on 50% of BP (Except during first 3 months of book launch).',
      },
    },
  })

  const { watch, setError, getValues } = form
  const expenseCap = watch('teamHiring.expenseCap')

  useEffect(() => {
    if (getValues('teamHiring.initialHeadcount') === undefined) {
      setError('teamHiring.initialHeadcount', { message: 'Required', type: 'required' })
    }
    if (getValues('teamHiring.hiringLimitBpThresholdMm') === undefined) {
      setError('teamHiring.hiringLimitBpThresholdMm', { message: 'Required', type: 'required' })
    }
  }, [expenseCap])

  const { trigger } = form

  useEffect(() => {
    trigger()
  }, [])

  const screenshotRef = React.useRef(null)
  const isUsTermSheet = config.region === Country.UnitedStates

  const onSubmit = async (values: z.infer<typeof pmTermSheetSchema>) => {
    if (isUsTermSheet) {
      await createTermSheet({
        type: 'UsPmTermSheet',
        payload: values,
      })
    } else {
      await createTermSheet({
        type: 'PmTermSheet',
        payload: values,
      })
    }

    // Save form values as JSON
    const jsonData = JSON.stringify(
      {
        ...values,
        meta: {
          type: 'term-sheet',
          region: config.region,
          candidateType: config.candidateType,
          isHiringManagerPrincipal: config.isHiringManagerPrincipal,
        },
      }, null, 2)
    downloadJsonFile(jsonData, termSheetFileName(values.personalInfo.fullName, 'PM'))
    captureScreenshot(screenshotRef, `TermSheet_${values.personalInfo.fullName}`)
  }


  return (
    <div ref={screenshotRef}>
      <PageLayout>
        <Typography.Title level={2}>PM Term Sheet</Typography.Title>
        <FormWrapper
          form={form}
          onSubmit={onSubmit}
          isLoading={isLoading}
          className={cn('w-fit', '[&>.form-field-wrapper]:w-60')}
        >
          <TermSheetImport isTermSheet={true} />
          <PersonalInfoSection {...personalInfoArgs} />
          <FormSection name="employmentDetails">
            <FormDatePicker2 name="startDate" />
            <FormInput name="position" />
            <FormInput name="location" />
          </FormSection>
          <PmTermSheetFinancialTerms />
          <FormSection name="deferredProgram">
            <FormNumberInput2 name="deferredRate" variant="percent" />
            <FormMoneyInput name="compensationThreshold" currencyOptions={config.usdOnly} />
            <FormNumberInput2 name="vestedYears" />
            <FormSelectAntd
              name="fundName"
              options={[{ label: 'Polymer Asia Fund LP', value: 'Polymer Asia Fund LP' }]}
            />
          </FormSection>
          <FormSection name="teamHiring">
            {isUsTermSheet && showDefaultTeamHiring &&
              <Button onClick={() => setShowDefaultTeamHiring(false)}>Enter Team Hiring conditions as free
                text</Button>}
            {isUsTermSheet && !showDefaultTeamHiring &&
              <Button onClick={() => setShowDefaultTeamHiring(true)}>Enter Team Hiring conditions as default</Button>}
            {!showDefaultTeamHiring && <FormInput name="usCustomFreeText" />}
            {showDefaultTeamHiring &&
              <>
                <FormMoneyInput name="expenseCap" currencyOptions={config.allCcyOptions} />
                <FormNumberInput2 name="initialHeadcount" />
                <FormDatePicker2 name="hiringLimitDate" description="If not applicable, leave blank" />
                <FormNumberInput2
                  name="hiringLimitBpThresholdMm"
                  label="Hiring Limit BP Threshold ($MM)"
                  description="In USD million"
                />
              </>}
          </FormSection>
          <RiskParametersSection {...riskParametersArgs} />
          <Button type="submit" disabled={isLoading}>Submit</Button>
        </FormWrapper>
      </PageLayout>
    </div>
  )
}

export default PmTermSheet
